import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  NgZone,
  OnDestroy,
  OnInit,
  ViewChild,
} from "@angular/core";
import { BehaviorSubject, Subscription, combineLatest } from "rxjs";
import { map } from "rxjs/operators";
import { FormControl, FormGroup } from "@angular/forms";
import { MatAutocompleteSelectedEvent } from "@angular/material/autocomplete";
import { SearchedInfo } from "../../model/searchedinfo";
import { SearchCommunicatorService } from "../../services/communicators/search-communicator.service";
import { LastSearchService } from "../../services/internal-services/lastsearch.service";

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: "app-search-bar",
  templateUrl: "./search-bar.component.html",
  styleUrls: ["./search-bar.component.scss"],
  standalone: false,
})
export class SearchBarComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild("addressInput", { static: true }) addressEl: ElementRef;

  form: FormGroup;

  autocomplete: google.maps.places.Autocomplete;

  geocoder: google.maps.Geocoder;

  subscriptions: Subscription[] = [];

  lastSearchedInfo: SearchedInfo[] = [];

  filteredSearches$ = new BehaviorSubject<SearchedInfo[]>([]);

  /**
   * Indica si hay texto en el input de búsqueda
   */
  private hasInputText$ = new BehaviorSubject<boolean>(false);

  /**
   * Indica si el autocomplete de Google Places (pac-container) está visible
   */
  private isGoogleAutocompleteVisible$ = new BehaviorSubject<boolean>(false);

  /**
   * Observable que determina si se debe mostrar el mat-autocomplete
   * Solo se muestra cuando no hay texto Y el autocomplete de Google no está visible
   */
  shouldShowMatAutocomplete$ = combineLatest([this.hasInputText$, this.isGoogleAutocompleteVisible$]).pipe(
    map(([hasText, isGoogleVisible]) => !hasText && !isGoogleVisible),
  );

  constructor(
    private zone: NgZone,
    protected searchCommunicatorService: SearchCommunicatorService,
    protected lastSearchService: LastSearchService,
    protected cdr: ChangeDetectorRef,
  ) {}

  ngOnInit() {
    const options: google.maps.places.AutocompleteOptions = {
      types: ["geocode"],
      fields: ["address_components", "geometry", "formatted_address"],
    };

    this.autocomplete = new google.maps.places.Autocomplete(this.addressEl.nativeElement, options);
    this.autocomplete.addListener("place_changed", this.onSubmit.bind(this));
    this.geocoder = new google.maps.Geocoder();
    this.form = new FormGroup({
      address: new FormControl(),
    });

    // Monitorear cambios en el input para detectar si hay texto
    this.subscriptions.push(
      this.form.get("address").valueChanges.subscribe((value) => {
        // Verificar que value es un string antes de usar trim()
        // Puede ser un objeto SearchedInfo cuando se selecciona una opción
        const hasText = !!value && typeof value === "string" && value.trim().length > 0;
        this.hasInputText$.next(hasText);
      }),
    );
    this.setupGoogleAutocompleteMutationObserver();
  }

  ngAfterViewInit() {
    this.subscriptions.push(
      this.lastSearchService.lastSearchs$.subscribe((lastSearchs: SearchedInfo[]) => {
        this.lastSearchedInfo = lastSearchs;
        // Actualizar el BehaviorSubject con las nuevas búsquedas
        this.filteredSearches$.next(this.lastSearchedInfo);
        this.cdr.detectChanges();
      }),
    );
    this.subscriptions.push(
      this.searchCommunicatorService.searchLocationResponse$.subscribe((searchedInfo) => {
        this.setAddressResultToMap(searchedInfo);
      }),
    );
  }

  private setupGoogleAutocompleteMutationObserver(): void {
    const observer = new MutationObserver(() => {
      const pacContainer = document.querySelector(".pac-container");
      if (pacContainer) {
        const isVisible = window.getComputedStyle(pacContainer).display !== "none";
        this.isGoogleAutocompleteVisible$.next(isVisible);
        this.cdr.detectChanges();
      } else {
        this.isGoogleAutocompleteVisible$.next(false);
      }
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ["style", "class"],
    });

    // Agregar observer a subscriptions para limpieza en ngOnDestroy
    this.subscriptions.push({
      unsubscribe: () => observer.disconnect(),
    } as Subscription);
  }

  ngOnDestroy() {
    this.subscriptions.forEach((sub: Subscription) => sub.unsubscribe());
  }

  onSubmit(): void {
    this.zone.run(() => {
      const place: google.maps.places.PlaceResult = this.autocomplete.getPlace();
      if (place && place.geometry) {
        const searchedInfo = new SearchedInfo().fromPlaceResult(place);
        this.fillInAddress(searchedInfo);
      }
      this.searchAddress();
    });
  }

  searchAddress() {
    const location = this.form.get("address").value;
    this.searchCommunicatorService.setSearchLocationRequest(location);
  }

  handleSearchInfo(search: SearchedInfo) {
    this.searchCommunicatorService.setSearchLocationRequest(search);
  }

  fillInAddress(searchedInfo: SearchedInfo): void {
    this.form
      .get("address")
      .setValue(searchedInfo.formattedAddress ? searchedInfo.formattedAddress : searchedInfo.location.toString());
    this.addressEl.nativeElement.value = searchedInfo.formattedAddress
      ? searchedInfo.formattedAddress
      : searchedInfo.location.toString();
    this.blurInputField();
  }

  blurInputField() {
    this.addressEl.nativeElement.blur();
  }

  setAddressResultToMap(searchedInfo: SearchedInfo): void {
    /* Set text to */
    this.fillInAddress(searchedInfo);
  }

  /**
   * Elimina una búsqueda del historial local
   * @param searchedInfo - La búsqueda a eliminar
   * @param event - Evento del click para prevenir propagación
   */
  deleteSearch(searchedInfo: SearchedInfo, event?: Event): void {
    if (event) {
      event.stopPropagation();
      event.preventDefault();
    }
    const index = this.lastSearchedInfo.indexOf(searchedInfo);
    if (index > -1) {
      this.lastSearchedInfo.splice(index, 1);
      this.filteredSearches$.next([...this.lastSearchedInfo]);
      this.cdr.detectChanges();
    }
  }

  /**
   * Maneja la selección de una opción del autocomplete de búsquedas recientes
   * @param event - Evento de selección del autocomplete
   */
  onOptionSelected(event: MatAutocompleteSelectedEvent): void {
    const selectedSearch: SearchedInfo = event.option.value;
    if (selectedSearch) {
      this.handleSearchInfo(selectedSearch);
    }
  }

  /**
   * Converts a SearchedInfo object or string to its display string for the autocomplete input.
   * Prevents [object Object] from flashing when a history item is selected.
   */
  displaySearch = (search: SearchedInfo | string): string => {
    if (!search) return "";
    if (typeof search === "string") return search;
    return search.formattedAddress ?? search.location?.toString() ?? "";
  };

  clearSearchText(): void {
    this.addressEl.nativeElement.value = "";
  }

  /**
   * Selects all text in the search input when clicked, allowing easy copy.
   */
  selectInputText(): void {
    this.addressEl.nativeElement.select();
  }
}
